/**
 * Übersicht (Dashboard) — 1:1 nach maestro_dashboard/code.html (Stitch):
 * 5 KPI-Kacheln, "Zu erledigen" (5/12) + "Anstehende Veranstaltungen" (7/12),
 * darunter Umsatzentwicklung (6 Monate) + Sales Funnel. Alle Zahlen aus echten API-Daten.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useUser } from "@stackframe/react";
import { apiFetch } from "../lib/api";
import { useApi } from "../hooks/useApi";
import type { EventRow, Inquiry, Payment } from "../ui/types";
import { EVENT_STATUS, money } from "../ui/format";
import { EmptyState, ErrorNote, Icon, KpiCard, LoadingRows, PageHeader, StatusPill } from "../ui/components";

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function hoursSince(iso: string): number {
  return Math.round((Date.now() - new Date(iso).getTime()) / 3_600_000);
}

/** Minuten menschlich: „42 Min" · „3 Std 12 Min" · „2 Tage". */
function fmtMinutes(m: number | null): string {
  if (m == null) return "–";
  if (m < 60) return `${m} Min`;
  if (m < 1440) return `${Math.floor(m / 60)} Std ${m % 60} Min`;
  return `${Math.round(m / 1440)} Tage`;
}

export default function DashboardPage() {
  const user = useUser();
  const firstName = user?.displayName?.trim().split(/\s+/)[0] ?? "";

  const inquiriesQ = useApi<Inquiry[]>("/api/inquiries");
  const eventsQ = useApi<EventRow[]>("/api/events");
  const nordsternQ = useApi<{
    offersSent: number;
    medianMinutes: number | null;
    fastestMinutes: number | null;
    hangingCount: number;
    openWithoutOffer: number;
  }>("/api/metrics/nordstern");

  const inquiries = inquiriesQ.data ?? [];
  const events = eventsQ.data ?? [];
  const loading = inquiriesQ.loading || eventsQ.loading;

  // Zahlungen ueber alle Events (fuer "Zahlung anmahnen" + ueberfaellig-Badge) -- gleiches Muster
  // wie payments.tsx/customers.tsx: kein globaler Endpunkt, daher parallel pro Event laden.
  const [overduePayments, setOverduePayments] = useState<(Payment & { eventId: string; eventLabel: string })[]>([]);
  const [overdueError, setOverdueError] = useState<string | null>(null);
  // A-12: stabile ID-Liste als Dependency (vorher [events.length] -> Aenderungen bei gleicher
  // Anzahl liessen die Zahlungen veraltet).
  const eventIds = events.map((e) => e.id).join(",");
  useEffect(() => {
    if (!events.length) return;
    let alive = true;
    (async () => {
      // A-12: Fan-out in try/catch - vorher liess ein einziger geworfener Netz-/Timeout-Fehler
      // die gesamte Promise.all-Kette unbehandelt scheitern, und die ueberfaelligen Zahlungen
      // (Mahn-Todos + Ueberfaellig-Badge) fielen ohne jeden Hinweis weg.
      try {
        const settled = await Promise.all(
          events.map(async (e) => {
            const res = await apiFetch(`/api/events/${e.id}/payments`);
            if (!res.ok) return [];
            const body = (await res.json()) as { data: Payment[] };
            return body.data
              .filter((p) => p.status === "pending" && p.dueDate && new Date(p.dueDate) < new Date())
              .map((p) => ({ ...p, eventId: e.id, eventLabel: e.occasion ?? e.number ?? "Event" }));
          }),
        );
        if (alive) {
          setOverduePayments(settled.flat());
          setOverdueError(null);
        }
      } catch (err) {
        if (alive) setOverdueError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventIds]);

  const now = new Date();
  const in30 = new Date(now.getTime() + 30 * 86_400_000);
  const in3days = new Date(now.getTime() + 3 * 86_400_000);

  const openInquiries = inquiries.filter((i) => i.status === "new").length;
  const pendingOffers = events.filter((e) => e.status === "offer_sent");
  const expiringOffers = pendingOffers.filter((e) => e.offerValidUntil && new Date(e.offerValidUntil) <= in3days);
  const next30 = events
    .filter(
      (e) =>
        e.eventDate &&
        new Date(e.eventDate) >= startOfMonth(now) &&
        new Date(e.eventDate) <= in30 &&
        !["cancelled", "completed"].includes(e.status),
    )
    .sort((a, b) => (a.eventDate! < b.eventDate! ? -1 : 1));
  const next30Confirmed = next30.filter((e) => ["offer_chosen", "paid", "completed"].includes(e.status)).length;
  const next30Requested = next30.filter((e) => ["inquiry", "offer_draft", "offer_sent"].includes(e.status)).length;
  const monthRevenueCents = events
    .filter(
      (e) =>
        ["paid", "completed"].includes(e.status) &&
        e.eventDate &&
        new Date(e.eventDate) >= startOfMonth(now),
    )
    .reduce((sum, e) => sum + (e.amountTotalCents ?? 0), 0);
  const outstandingEvents = events.filter((e) => e.status === "offer_chosen");
  const outstandingCents = outstandingEvents.reduce((s, e) => s + (e.amountTotalCents ?? 0), 0);

  const staleInquiries = inquiries.filter(
    (i) => i.status === "new" && Date.now() - new Date(i.createdAt).getTime() > 48 * 3_600_000,
  );
  const awaitingChoice = events.filter((e) => e.status === "offer_sent");

  // „Zu erledigen" (1c): eine einheitliche Checkbox-Liste aus drei Quellen, je Zeile
  // Titel + Kontext + rechts eine Ton-Pille. Max. 6 Einträge, Reihenfolge nach Dringlichkeit.
  type TodoTone = "red" | "amber" | "neutral";
  type TodoRow = { key: string; to: string; title: string; sub: string; pill: { text: string; tone: TodoTone } };
  const todoRows: TodoRow[] = [
    ...overduePayments.slice(0, 4).map((p) => ({
      key: `pay-${p.id}`,
      to: `/veranstaltungen/${p.eventId}`,
      title: `Zahlung anmahnen — ${p.eventLabel}`,
      sub: money(p.amountCents),
      pill: {
        text: p.dueDate
          ? `${Math.max(1, Math.round((Date.now() - new Date(p.dueDate).getTime()) / 86_400_000))} Tage überfällig`
          : "überfällig",
        tone: "red" as TodoTone,
      },
    })),
    ...staleInquiries.slice(0, 4).map((i) => ({
      key: `inq-${i.id}`,
      // CX-Fix 2026-07-18: Deep-Link in den Drawer DIESER Anfrage (?open=), nicht nur zur Liste.
      to: `/anfragen?open=${i.id}`,
      title: `Angebot erstellen — ${i.customerName}`,
      sub: i.guests ? `${i.guests} Gäste` : "Neue Anfrage",
      pill: { text: `${hoursSince(i.createdAt)} h`, tone: "red" as TodoTone },
    })),
    ...awaitingChoice.slice(0, 4).map((e) => ({
      key: `evt-${e.id}`,
      to: `/veranstaltungen/${e.id}`,
      title: `Antwort abwarten — ${e.occasion ?? e.number ?? "Event"}`,
      sub: `Angebot v${e.currentOfferVersion} gesendet`,
      pill: { text: "wartet", tone: "neutral" as TodoTone },
    })),
  ].slice(0, 6);

  const TODO_PILL: Record<TodoTone, string> = {
    red: "bg-[#F8E7E3] text-[#B23B32] dark:bg-[#B23B32]/22 dark:text-[#e39b92]",
    amber: "bg-[#FAEFD9] text-[#96650D] dark:bg-[#C98A1B]/20 dark:text-[#e0b366]",
    neutral: "bg-[#F4EEE1] text-[#6F675A] dark:bg-white/[0.08] dark:text-[#b8ae9e]",
  };

  // Funnel-Kennzahlen: Abschlussquote (Gewonnen / Neue Anfragen) + Ø Tage bis zum Angebotsversand.
  const wonCount = events.filter((e) => ["offer_chosen", "paid", "completed"].includes(e.status)).length;
  const closeRate = inquiries.length > 0 ? Math.round((wonCount / inquiries.length) * 100) : null;
  const offerLeadDays = events
    .filter((e) => e.offerSentAt)
    .map((e) => (new Date(e.offerSentAt!).getTime() - new Date(e.createdAt).getTime()) / 86_400_000);
  const avgOfferLeadDays = offerLeadDays.length > 0 ? offerLeadDays.reduce((a, b) => a + b, 0) / offerLeadDays.length : null;

  // Umsatz 6 Monate (bezahlte/durchgefuehrte Events nach Event-Datum)
  const months: { key: string; label: string; cents: number }[] = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({
      key: `${d.getFullYear()}-${d.getMonth()}`,
      label: d.toLocaleDateString("de-DE", { month: "short" }),
      cents: 0,
    });
  }
  for (const e of events) {
    if (!e.eventDate || !["paid", "completed"].includes(e.status)) continue;
    const d = new Date(e.eventDate);
    const m = months.find((x) => x.key === `${d.getFullYear()}-${d.getMonth()}`);
    if (m) m.cents += e.amountTotalCents ?? 0;
  }
  const maxCents = Math.max(1, ...months.map((m) => m.cents));

  // CX-Audit 2026-07-17: Funnel-Stufen sind klickbar — jede Stufe führt zur Liste dahinter.
  const funnel = [
    { label: "Neue Anfragen", n: inquiries.filter((i) => i.status === "new").length, w: "100%", tone: "low", to: "/anfragen" },
    { label: "Angebote erstellt", n: pendingOffers.length, w: "83%", tone: "mid", to: "/veranstaltungen?filter=offer_sent" },
    {
      label: "Gewonnen",
      n: wonCount,
      w: "66%",
      tone: "win",
      to: "/veranstaltungen?filter=won",
    },
  ];

  return (
    <>
      <PageHeader
        title={firstName ? `Willkommen zurück, ${firstName}` : "Willkommen zurück"}
        subtitle={`${now.toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long" })}${openInquiries > 0 ? ` · ${openInquiries} neue Anfrage${openInquiries === 1 ? "" : "n"}` : ""}`}
        actions={
          <Link to="/anfragen?neu=1" className="btn-primary">
            <Icon name="add" className="text-[18px]" /> Neue Anfrage
          </Link>
        }
      />

      {/* A-12: Teil-/Totalausfall des Zahlungs-Fan-outs sichtbar machen, statt überfällige Posten still zu verschlucken. */}
      {overdueError && (
        <div className="mb-4">
          <ErrorNote message={`Überfällige Zahlungen konnten nicht vollständig geladen werden: ${overdueError}`} />
        </div>
      )}

      {/* KPI-Grid (5 Kacheln) — CX-Audit 2026-07-17: jede Kachel beantwortet ihre Frage per
          Klick (gefilterte Liste/Kalender/Zahlungen) statt reiner Anzeige-Sackgasse. */}
      <div className="mb-4 grid grid-cols-2 gap-3.5 md:grid-cols-3 lg:grid-cols-5">
        <KpiCard
          label="Offene Anfragen"
          value={openInquiries}
          icon="inbox"
          to="/anfragen"
          delta={staleInquiries.length > 0 ? { text: `${staleInquiries.length} überfällig`, tone: "warn" } : undefined}
        />
        <KpiCard
          label="Ausstehende Angebote"
          value={pendingOffers.length}
          icon="description"
          to="/veranstaltungen?filter=offer_sent"
          delta={expiringOffers.length > 0 ? { text: `${expiringOffers.length} enden bald`, tone: "warn" } : undefined}
        />
        <KpiCard
          label="Events (30 Tage)"
          value={next30.length}
          icon="event"
          to="/kalender"
          delta={next30.length > 0 ? { text: `${next30Confirmed} fix · ${next30Requested} angefragt`, tone: "neutral" } : undefined}
        />
        <KpiCard label="Umsatz Monat" value={money(monthRevenueCents)} icon="payments" to="/zahlungen?filter=paid" />
        <KpiCard
          label="Offene Beträge"
          value={money(outstandingCents)}
          icon="account_balance_wallet"
          to="/zahlungen?filter=open"
          delta={overduePayments.length > 0 ? { text: `${overduePayments.length} überfällig`, tone: "down" } : outstandingEvents.length > 0 ? { text: `${outstandingEvents.length} offen`, tone: "warn" } : undefined}
        />
      </div>

      {/* Nordstern: Minuten von Anfrage bis Angebot versendet (der produktisierte Vorsprung). */}
      {nordsternQ.data && (
        <div className="mb-4 overflow-hidden rounded-2xl border border-primary/20 bg-primary/5 p-6 dark:border-primary-fixed-dim/20 dark:bg-primary-fixed-dim/5">
          <div className="flex flex-wrap items-center justify-between gap-6">
            <div>
              <p className="flex items-center gap-2 font-label-md text-label-md uppercase tracking-wider text-primary dark:text-primary-fixed-dim">
                <Icon name="bolt" className="text-[18px]" /> Antwortgeschwindigkeit
              </p>
              <p className="mt-2 font-display-lg text-3xl font-bold text-on-background dark:text-inverse-on-surface md:text-display-lg">
                {fmtMinutes(nordsternQ.data.medianMinutes)}
              </p>
              <p className="mt-1 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                Median von Anfrage bis Angebot · {nordsternQ.data.offersSent} Angebote
                {nordsternQ.data.fastestMinutes != null && ` · schnellstes ${fmtMinutes(nordsternQ.data.fastestMinutes)}`}
              </p>
            </div>
            {nordsternQ.data.hangingCount > 0 ? (
              // CX-Audit 2026-07-17: Die Warnung war reine Anzeige — der Klick führt jetzt
              // direkt zu den liegengebliebenen Anfragen (dorthin, wo man handeln kann).
              <Link
                to="/anfragen"
                className="rounded-xl bg-yellow-100 px-5 py-3 text-center transition-shadow hover:shadow-md dark:bg-yellow-400/15"
              >
                <p className="font-headline-sm text-headline-sm font-bold text-yellow-800 dark:text-yellow-200">
                  {nordsternQ.data.hangingCount}
                </p>
                <p className="font-body-sm text-body-sm text-yellow-800 dark:text-yellow-200">
                  Anfrage(n) &gt; 4 h ohne Angebot — jetzt reagieren
                </p>
              </Link>
            ) : (
              <div className="flex items-center gap-2 rounded-xl bg-green-100 px-5 py-3 text-green-800 dark:bg-green-400/15 dark:text-green-200">
                <Icon name="check_circle" className="text-[20px]" />
                <span className="font-body-md text-body-md">Keine liegengebliebenen Anfragen</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Zwei-Spalten (1c): „Zu erledigen" (1.25fr, breiter) + „Anstehende Veranstaltungen" (1fr) */}
      <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-[1.25fr_1fr]">
        <div className="card flex flex-col overflow-hidden">
          <div className="flex items-center gap-2 border-b border-[#F2ECDF] px-[18px] py-3.5 dark:border-white/[0.06]">
            <span className="text-[14.5px] font-semibold text-on-background dark:text-inverse-on-surface">Zu erledigen</span>
            <span className="rounded-full bg-[#F4EEE1] px-[7px] py-0.5 text-[10.5px] font-semibold text-[#6F675A] dark:bg-white/[0.08] dark:text-secondary-fixed-dim">
              {todoRows.length}
            </span>
            <Link to="/anfragen" className="ml-auto text-[12px] font-medium text-primary hover:underline">
              Alle ansehen
            </Link>
          </div>
          {loading && <LoadingRows rows={3} />}
          {!loading && todoRows.length === 0 && (
            <EmptyState icon="task_alt" title="Alles erledigt" hint="Keine überfälligen Aufgaben." />
          )}
          {!loading &&
            todoRows.map((t) => (
              <Link
                key={t.key}
                to={t.to}
                className="flex items-center gap-3 border-b border-[#F6F0E4] px-[18px] py-3 transition-colors last:border-b-0 hover:bg-[#FBF6EC] dark:border-white/[0.05] dark:hover:bg-white/[0.03]"
              >
                <span className="h-4 w-4 shrink-0 rounded-[5px] border-[1.5px] border-[#D6CCB9] dark:border-white/25" aria-hidden />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[13px] font-semibold text-on-background dark:text-inverse-on-surface">
                    {t.title}
                  </div>
                  <div className="mt-0.5 truncate text-[11.5px] text-[#6F675A] dark:text-secondary-fixed-dim">{t.sub}</div>
                </div>
                <span className={`shrink-0 whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-semibold ${TODO_PILL[t.pill.tone]}`}>
                  {t.pill.text}
                </span>
              </Link>
            ))}
        </div>

        <div className="card flex flex-col overflow-hidden">
          <div className="flex items-center gap-2 border-b border-[#F2ECDF] px-[18px] py-3.5 dark:border-white/[0.06]">
            <span className="text-[14.5px] font-semibold text-on-background dark:text-inverse-on-surface">
              Anstehende Veranstaltungen
            </span>
            <Link to="/kalender" className="ml-auto text-[12px] font-medium text-primary hover:underline">
              Kalender
            </Link>
          </div>
          {loading ? (
            <LoadingRows rows={3} />
          ) : next30.length === 0 ? (
            <EmptyState
              icon="event_upcoming"
              title="Keine Events in den nächsten 30 Tagen"
              hint="Bestätigte Termine erscheinen hier."
            />
          ) : (
            next30.slice(0, 6).map((e) => {
              const confirmed = ["offer_chosen", "paid", "completed"].includes(e.status);
              const d = e.eventDate ? new Date(e.eventDate) : null;
              return (
                <Link
                  key={e.id}
                  to={`/veranstaltungen/${e.id}`}
                  className="flex items-center gap-3.5 border-b border-[#F6F0E4] px-[18px] py-2.5 transition-colors last:border-b-0 hover:bg-[#FBF6EC] dark:border-white/[0.05] dark:hover:bg-white/[0.03]"
                >
                  <div className={`w-[42px] shrink-0 rounded-[9px] py-1 text-center ${confirmed ? "bg-tertiary-container" : "bg-[#FAEFD9]"}`}>
                    <div className={`font-bold leading-[1.1] ${confirmed ? "text-[#1D6A45]" : "text-[#96650D]"}`} style={{ fontSize: "15px" }}>
                      {d ? d.getDate() : "–"}
                    </div>
                    <div className={`text-[9.5px] font-semibold tracking-wide ${confirmed ? "text-[#579B78]" : "text-[#C1985B]"}`}>
                      {d ? d.toLocaleDateString("de-DE", { month: "short" }).toUpperCase().replace(".", "") : ""}
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[13px] font-semibold text-on-background dark:text-inverse-on-surface">
                      {e.occasion ?? e.number ?? "Event"}
                    </p>
                    <p className="truncate text-[11.5px] text-[#6F675A] dark:text-secondary-fixed-dim">
                      {e.guestCount ? `${e.guestCount} Gäste` : ""}
                      {e.guestCount && e.location ? " · " : ""}
                      {e.location ?? ""}
                    </p>
                  </div>
                  <StatusPill meta={EVENT_STATUS[e.status]} />
                </Link>
              );
            })
          )}
        </div>
      </div>

      {/* Umsatzentwicklung + Anfragen-Funnel (1c): Karten-Kopf im Kopf, Balken-Radius 7px */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.5fr_1fr]">
        <div className="card flex flex-col overflow-hidden">
          <div className="flex items-center gap-2 border-b border-[#F2ECDF] px-[18px] py-3.5 dark:border-white/[0.06]">
            <span className="text-[14.5px] font-semibold text-on-background dark:text-inverse-on-surface">Umsatz</span>
            <span className="ml-auto text-[11.5px] text-[#9A9080] dark:text-secondary-fixed-dim">Letzte 6 Monate</span>
          </div>
          <div className="flex flex-1 flex-col px-6 pb-3.5 pt-4">
            <div className="relative flex h-[168px] items-end gap-2">
              {months.map((m, idx) => {
                const isCurrent = idx === months.length - 1;
                const opacity = isCurrent ? 1 : Math.min(0.6, 0.2 + (m.cents / maxCents) * 0.4);
                return (
                  <div key={m.key} className="flex h-full w-1/6 flex-col items-center justify-end gap-1.5">
                    <span className={`whitespace-nowrap text-[10px] ${isCurrent ? "font-semibold text-primary" : "font-medium text-[#9A9080] dark:text-secondary-fixed-dim"}`}>
                      {money(m.cents)}
                    </span>
                    <div
                      className="w-full max-w-[44px] bg-primary"
                      style={{ height: `${Math.max(4, (m.cents / maxCents) * 100)}%`, opacity, borderRadius: "7px 7px 3px 3px" }}
                    />
                  </div>
                );
              })}
            </div>
            <div className="mt-2 flex justify-between text-[11px] font-medium text-[#6F675A] dark:text-secondary-fixed-dim">
              {months.map((m, idx) => (
                <span key={m.key} className={idx === months.length - 1 ? "font-semibold text-on-background dark:text-inverse-on-surface" : ""}>
                  {m.label}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="card flex flex-col overflow-hidden">
          <div className="flex items-center gap-2 border-b border-[#F2ECDF] px-[18px] py-3.5 dark:border-white/[0.06]">
            <span className="text-[14.5px] font-semibold text-on-background dark:text-inverse-on-surface">Anfragen-Funnel</span>
            <span className="ml-auto text-[11.5px] text-[#9A9080] dark:text-secondary-fixed-dim">Letzte 90 Tage</span>
          </div>
          <div className="flex flex-1 flex-col gap-3 p-[18px]">
            {funnel.map((f) => (
              <div key={f.label} className="flex flex-col gap-1.5">
                <p className="text-[12px] font-medium text-[#6F675A] dark:text-secondary-fixed-dim">{f.label}</p>
                <Link
                  to={f.to}
                  title={`${f.label} ansehen`}
                  className={`flex h-7 items-center rounded-lg px-2.5 text-[11.5px] font-bold transition-opacity hover:opacity-80 ${
                    f.tone === "low"
                      ? "bg-[#E7EEF7] text-[#31619B] dark:bg-[#31619B]/25 dark:text-[#9dbde3]"
                      : f.tone === "mid"
                        ? "bg-[#FAEFD9] text-[#96650D] dark:bg-[#C98A1B]/20 dark:text-[#e0b366]"
                        : "bg-[#2E9963] text-white"
                  }`}
                  style={{ width: f.w }}
                >
                  {f.n}
                </Link>
              </div>
            ))}
            {(closeRate != null || avgOfferLeadDays != null) && (
              <div className="mt-auto flex items-center justify-between gap-4 border-t border-[#F2ECDF] pt-3 text-[11.5px] font-medium text-[#6F675A] dark:border-white/[0.06] dark:text-secondary-fixed-dim">
                {closeRate != null && <span>Abschlussquote {closeRate} %</span>}
                {avgOfferLeadDays != null && (
                  <span>
                    Ø {avgOfferLeadDays < 1 ? "< 1" : avgOfferLeadDays.toFixed(1).replace(".", ",")} Tage bis zum Angebot
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
